/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Button, Card, Input, Table } from "core/components";
import { InventoryItemFormData, WarehouseFormData } from "core/schema";
import SupplierItemForm from "./SupplierItemForm";
import formatter from "core/formatter";
import pluralize from "pluralize";
import { api } from "core/api";
import { remove_inventory_item_admin_supplier_path } from "routes";

type SupplierItemsForm = {
  inventory_items: Array<InventoryItemFormData>;
  warehouses: Array<WarehouseFormData>;
  credit_terms: Array<InventoryItemFormData>;
};

const SupplierItemsForm = ({ supplier, inventory_items, warehouses, credit_terms }: SupplierItemsForm): JSX.Element => {
  const [showSupplierItemModal, setShowSupplierModal] = React.useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = React.useState(false);
  const [supplierItems, setSuppplierItems] = React.useState(supplier.supplier_items);
  const [currentItemSupplier, setCurrentItemSupplier] = React.useState(null);
  const table = React.useRef(null);

  const addSupplierItem = (item) => {
    const foundIndex = supplierItems.findIndex((supplierItem) => supplierItem.id == item.id);

    // Check if it is updated item
    if (foundIndex != -1) {
      supplierItems[foundIndex] = item;
      setSuppplierItems([...supplierItems]);
    } else {
      setSuppplierItems([...supplierItems, item]);
    }
  };

  const removeSupplierItem = (item) => {
    setSuppplierItems(supplierItems.filter((supplierItem) => supplierItem.id != item.id));
  };

  const remove = async () => {
    try {
      await api.deleteItem(remove_inventory_item_admin_supplier_path({ id: supplier.id }), {
        data: { item_supplier: { id: currentItemSupplier?.id } }
      });

      removeSupplierItem(currentItemSupplier);
      setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
    } catch (error) {
      console.error(error);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Inventory ID",
        accessor: "inventory_item.code",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Inventory",
        accessor: "inventory_item.name",
        Cell: (props) => {
          return (
            <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} data-toggle="tooltip" title={props.value}>
              {props.value}
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure.code",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        Header: "Lead Time",
        accessor: "lead_time",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{pluralize("Day", props.value, true)}</span>
            </div>
          );
        }
      },
      {
        Header: "Standard Price",
        accessor: "standard_price",
        Cell: (props) => {
          return (
            <div className="text-right">
              <span>{formatter.format(props.value)}</span>
            </div>
          );
        }
      },
      {
        id: "current_price",
        Header: "Current Price",
        accessor: "current_price",
        Cell: (props) => {
          return (
            <div className="text-right">
              <span>{formatter.format(props.value)}</span>
            </div>
          );
        }
      },
      {
        Header: "Min. Order Quantity",
        accessor: "min_order_quantity",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        Header: "Credit Term",
        accessor: "credit_term.code",
        Cell: (props) => {
          return (
            <div className="text-center">
              <span>{props.value}</span>
            </div>
          );
        }
      },
      {
        id: "actions",
        style: {
          width: "70px"
        },
        Cell: (props) => {
          return (
            <>
              <span title="Edit Inventory Item" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action-success"
                  onClick={() => {
                    setCurrentItemSupplier(props.row.original);
                    setShowSupplierModal(!showSupplierItemModal);
                  }}
                >
                  <div className="svg-icon h6">
                    <svg className="icon icon-edit ">
                      <use xlinkHref="#edit"></use>
                    </svg>
                  </div>
                </a>
              </span>
              <span title="Delete Inventory Item" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action-danger"
                  onClick={() => {
                    setCurrentItemSupplier(props.row.original);
                    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
                  }}
                >
                  <div className="svg-icon h6">
                    <svg className="icon icon-delete ">
                      <use xlinkHref="#delete"></use>
                    </svg>
                  </div>
                </a>
              </span>
            </>
          );
        }
      }
    ],
    []
  );

  return (
    <React.StrictMode>
      <Card.Body>
        <Container fluid={true}>
          <Row>
            <Col>
              <Input placeholder="Search for Inventory ID" onChange={(e) => table.current.setFilter(1, e.target.value)} />
            </Col>
            <Col>
              <Input placeholder="Search for Inventory Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
            </Col>
            <Col>
              <Button
                className="button--brand text-right float-end mb-2"
                title="Add Inventory Item"
                onClick={() => {
                  setCurrentItemSupplier(null);
                  setShowSupplierModal(!showSupplierItemModal);
                }}
              />
            </Col>
          </Row>
          <Row>
            {supplierItems.length > 0 ? (
              <Table columns={columns} data={supplierItems} ref={table} />
            ) : (
              <div className="text-center mt-4 mb-4">No inventory items yet.</div>
            )}
          </Row>
        </Container>
      </Card.Body>
      <SupplierItemForm
        supplier={supplier}
        inventory_items={
          !currentItemSupplier?.id
            ? inventory_items.filter((inventory_item) => !supplierItems.find((supplierItem) => inventory_item.id == supplierItem?.inventory_item?.id))
            : inventory_items
        }
        warehouses={warehouses}
        credit_terms={credit_terms}
        currentItemSupplier={currentItemSupplier}
        show={showSupplierItemModal}
        onClose={() => setShowSupplierModal(!showSupplierItemModal)}
        onSave={(supplierItem) => {
          addSupplierItem(supplierItem);
          setShowSupplierModal(!showSupplierItemModal);
        }}
      />
      <Modal dialogClassName="modal-dialog-centered" show={showDeleteConfirmationModal}>
        <Modal.Header>{`Remove inventory item: ${currentItemSupplier?.inventory_item?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to remove inventory item ${currentItemSupplier?.inventory_item?.name}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button className="button--danger" icon="save" title="Yes, remove inventory item." onClick={() => remove()} />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default SupplierItemsForm;
